/**
 * RRULE canonicalisation, for COMPARISON ONLY — never for storage or for
 * sending to a provider. The stored string stays exactly as the user's
 * calendar wrote it.
 *
 * Moved verbatim from the sync engine's `normalize.ts` (which now re-exports
 * this module) and extended to be MULTI-LINE AWARE: the vault `rrule` column
 * can hold a full `DTSTART:…\nRRULE:…\nEXDATE:…` fragment, and comparing
 * those raw against a provider's bare rule reported spurious differences.
 * The extension canonicalises only the RRULE line; every other line is kept
 * byte-stable in its original order, so a single-line rule and a multi-line
 * value still compare unequal unless they are genuinely the same component.
 *
 * RFC 5545 fixes no part order and no case, so the same rule has many
 * equally valid spellings, and providers each pick their own. Round-tripping
 * one calendar's rule through Microsoft Graph rewrites it — Graph models
 * recurrence as a structured object, and serialising that object back cannot
 * reproduce the incoming spelling. Measured against a real account: 19 of 25
 * distinct RRULEs came back differing only in part order.
 *
 * Comparing those raw made the three-way merge report a `recurrence` change
 * on a series nobody had touched; the resulting rewrite ping-ponged between
 * two providers syncing the same calendar — one pointless write to two of
 * the user's real calendars per recurring event.
 *
 * DELIBERATELY CONSERVATIVE. Provider-agnostic, so it may only fold
 * differences that are pure SERIALISATION — part order, case, member order
 * within a set, and an explicit INTERVAL=1. It must NOT coarsen anything a
 * provider can genuinely represent. In particular UNTIL keeps its full
 * precision: Graph's range stores only a date, but Google and CalDAV store
 * the instant, and treating same-day UNTIL times as equal here would
 * silently refuse to push a real edit on those providers.
 *
 * A malformed rule is returned untouched rather than half-parsed — comparing
 * raw is the safe failure, since it can only over-report a change.
 */

/** RRULE parts whose value is an unordered SET — order carries no meaning. */
const RRULE_SET_VALUED = new Set([
  'BYDAY',
  'BYMONTH',
  'BYMONTHDAY',
  'BYSETPOS',
  'BYHOUR',
  'BYMINUTE',
  'BYSECOND',
  'BYWEEKNO',
  'BYYEARDAY',
]);

/** Parts whose value is the RFC default, so present and absent mean the same. */
const RRULE_DEFAULTS: Record<string, string> = { INTERVAL: '1' };

function canonicalRruleValue(value: string): string {
  const parts = new Map<string, string>();
  for (const segment of value.split(';')) {
    const trimmed = segment.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) return value; // Not `KEY=VALUE` — don't guess.
    const key = trimmed.slice(0, eq).toUpperCase();
    // Every RRULE value is a keyword, number, or UTC timestamp: all
    // case-insensitive, none carrying user text.
    let partValue = trimmed.slice(eq + 1).toUpperCase();
    if (RRULE_SET_VALUED.has(key)) {
      partValue = partValue
        .split(',')
        .map((member) => member.trim())
        .filter(Boolean)
        .sort()
        .join(',');
    }
    if (RRULE_DEFAULTS[key] === partValue) continue;
    parts.set(key, partValue);
  }
  if (parts.size === 0) return value;

  return [...parts.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, partValue]) => `${key}=${partValue}`)
    .join(';');
}

export function canonicalRrule(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) return value;

  // Multi-line component form: canonicalise ONLY the RRULE line; every other
  // line stays byte-stable in its original order.
  if (/\r?\n/.test(value)) {
    return value
      .split(/\r?\n/)
      .map((line) =>
        /^RRULE:/i.test(line.trim())
          ? `RRULE:${canonicalRruleValue(line.trim().slice('RRULE:'.length))}`
          : line,
      )
      .join('\n');
  }

  // Single-line values keep the historical behavior byte-for-byte — a
  // `RRULE:`-prefixed value is deliberately NOT folded to its bare form
  // here; that would change what the sync engine's diff reports for rows
  // stored with the prefix, and this function only ever moves, it does not
  // redesign.
  return canonicalRruleValue(value);
}

/** Canonical UTC ISO-8601 with milliseconds — the occurrence-key form. */
function canonicalIso(value: string | Date): string {
  return new Date(value).toISOString();
}

/** Parses provider EXDATE values ('20260615', '20260615T120000Z',
 * '2026-06-15T12:00:00Z') to occurrence keys. Unparseable values throw
 * RangeError → convert-stage deterministic failure. */
export function exdateToOccurrenceKey(exdate: string): string {
  const compact = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/.exec(exdate.trim());
  if (compact) {
    const [, y, m, d, hh, mm, ss] = compact;
    const iso = hh !== undefined ? `${y}-${m}-${d}T${hh}:${mm}:${ss}Z` : `${y}-${m}-${d}T00:00:00Z`;
    return canonicalIso(iso);
  }
  return canonicalIso(exdate);
}
